/**
 * Marketplace Zustand Store
 * BP-08-04: Marketplace Frontend (Family)
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  MarketplaceState,
  MarketplaceFilters,
  MarketplaceSearchResults,
  VendorCategory,
} from '@/features/marketplace/types/marketplaceTypes';

const DEFAULT_FILTERS: MarketplaceFilters = {
  sortBy: 'relevance',
};

const MAX_RECENTLY_VIEWED = 10;

export const useMarketplaceStore = create<MarketplaceState>()(
  persist(
    (set, get) => ({
      // Search state
      searchQuery: '',
      searchResults: null,
      isSearching: false,
      
      // Filter state
      filters: DEFAULT_FILTERS,
      activeCategory: null,
      
      // Recently viewed
      recentlyViewedProducts: [],
      recentlyViewedVendors: [],
      
      // Wishlist
      wishlistProductIds: [],
      
      // Actions
      setSearchQuery: (query: string) => {
        set({ searchQuery: query });
      },
      
      setSearchResults: (results: MarketplaceSearchResults | null) => {
        set({ searchResults: results, isSearching: false });
      },
      
      setFilters: (newFilters: Partial<MarketplaceFilters>) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        }));
      },
      
      resetFilters: () => {
        set({ filters: DEFAULT_FILTERS });
      },
      
      setActiveCategory: (category: VendorCategory | null) => {
        set({ activeCategory: category });
      },
      
      addRecentlyViewedProduct: (productId: string) => {
        set((state) => {
          const filtered = state.recentlyViewedProducts.filter((id) => id !== productId);
          return {
            recentlyViewedProducts: [productId, ...filtered].slice(0, MAX_RECENTLY_VIEWED),
          };
        });
      },
      
      addRecentlyViewedVendor: (vendorId: string) => {
        set((state) => {
          const filtered = state.recentlyViewedVendors.filter((id) => id !== vendorId);
          return {
            recentlyViewedVendors: [vendorId, ...filtered].slice(0, MAX_RECENTLY_VIEWED),
          };
        });
      },
      
      toggleWishlist: (productId: string) => {
        set((state) => {
          const isInList = state.wishlistProductIds.includes(productId);
          return {
            wishlistProductIds: isInList
              ? state.wishlistProductIds.filter((id) => id !== productId)
              : [...state.wishlistProductIds, productId],
          };
        });
      },
      
      isInWishlist: (productId: string) => {
        return get().wishlistProductIds.includes(productId);
      },
      
      clearRecentlyViewed: () => {
        set({
          recentlyViewedProducts: [],
          recentlyViewedVendors: [],
        });
      },
    }),
    {
      name: 'brightpath-marketplace',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        recentlyViewedProducts: state.recentlyViewedProducts,
        recentlyViewedVendors: state.recentlyViewedVendors,
        wishlistProductIds: state.wishlistProductIds,
      }),
    }
  )
);

// Selectors for optimized re-renders
export const useSearchQuery = () => useMarketplaceStore((state) => state.searchQuery);
export const useSearchResults = () => useMarketplaceStore((state) => state.searchResults);
export const useMarketplaceFilters = () => useMarketplaceStore((state) => state.filters);
export const useActiveCategory = () => useMarketplaceStore((state) => state.activeCategory);
export const useRecentlyViewedProducts = () => 
  useMarketplaceStore((state) => state.recentlyViewedProducts);
export const useRecentlyViewedVendors = () => 
  useMarketplaceStore((state) => state.recentlyViewedVendors);
export const useWishlistIds = () => useMarketplaceStore((state) => state.wishlistProductIds);

// Action selectors
export const useMarketplaceActions = () =>
  useMarketplaceStore((state) => ({
    setSearchQuery: state.setSearchQuery,
    setSearchResults: state.setSearchResults,
    setFilters: state.setFilters,
    resetFilters: state.resetFilters,
    setActiveCategory: state.setActiveCategory,
    addRecentlyViewedProduct: state.addRecentlyViewedProduct,
    addRecentlyViewedVendor: state.addRecentlyViewedVendor,
    toggleWishlist: state.toggleWishlist,
    isInWishlist: state.isInWishlist,
    clearRecentlyViewed: state.clearRecentlyViewed,
  }));

